import { useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import FingerprintSDK from '@cx-sdk/fp';
import RootErrorPage, { DEFAULT_RESOURCES_URL, extractException } from '@cx-web/components/composites/RootErrorPage';
import { type MyOCData } from '@cx-web/domain-types/myoc';
import { getRepubFromURL, isErrorWithStatus } from '@cx-web/utils';
import { getClientI18n } from '@cx-web/utils/getClientI18n';
import { useLoaderData, useLocation, useRouteError, useRouteLoaderData } from '@remix-run/react';
import { captureRemixErrorBoundaryError, withSentry } from '@sentry/remix';

import { PlusConfigProvider } from '~/apps/product-plus/providers/plus-config-context';
import { BetslipProvider } from '~/providers/betslip-context';
import { FeatureFlagProvider, featureFlags } from '~/providers/feature-flag-provider';
import { MyOCUserProvider } from '~/providers/myoc-user';
import { RepubProvider } from '~/providers/repub-context';
import { ServicesProvider } from '~/providers/services-context';
import { type loader as rootLoader } from '~/root-loader';

import Body from './body';
import Head from './head';
import { type loader } from './root-loader';

export { loader } from './root-loader';

/**
 * Shared root error boundary. It must never fail: it reads only the lightweight
 * `'root'` loader (repub from the OC-Repub header, resourcesUrl from env) and falls
 * back to the URL-derived repub and the CDN default when that data is absent — which
 * is exactly when an API caused the error. RootErrorPage renders the whole document
 * from those inputs alone.
 */
export function ErrorBoundary() {
  const error = useRouteError();
  captureRemixErrorBoundaryError(extractException(error));

  const rootLoaderData = useRouteLoaderData<typeof rootLoader>('root');
  const location = useLocation();
  const repub = rootLoaderData?.repub ?? getRepubFromURL(new URL(`https://www.oddschecker.com${location.pathname}`));
  const errorStatus = isErrorWithStatus(error) ? error.status : null;

  return (
    <RootErrorPage
      errorStatus={errorStatus}
      repub={repub}
      resourcesUrl={rootLoaderData?.resourcesUrl ?? DEFAULT_RESOURCES_URL}
    />
  );
}

function App() {
  const {
    ENV,
    featureToggleDefinitions,
    geolocationInfo,
    labels,
    lng,
    myocData,
    oddsFormat,
    persistedBetslipData,
    repub,
    resources,
    siteConfig,
  } = useLoaderData<typeof loader>();

  const i18n = getClientI18n({ lng, resources });

  useEffect(() => {
    const fingerprintingEnabled = featureToggleDefinitions.find(
      def => def.name === featureFlags.SUBS__FINGERPRINTING,
    )?.enabled;
    if (!fingerprintingEnabled) {
      return;
    }

    async function fingerprintClient() {
      if (localStorage && !localStorage.getItem('fpsdk-set')) {
        try {
          const fingerprintSDK = await FingerprintSDK.load();
          const result = await fingerprintSDK.get();
          const response = await fetch('/api/cx/fp', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(result),
          });
          const payload = (await response.json()) as { visitorId: string };
          if (payload.visitorId) {
            localStorage.setItem('fpsdk-set', payload.visitorId);
          }
        } catch (error) {}
      }
    }
    fingerprintClient();
  }, [featureToggleDefinitions]);

  return (
    <I18nextProvider i18n={i18n}>
      <html lang={lng}>
        <FeatureFlagProvider featureToggleDefinitions={featureToggleDefinitions}>
          <RepubProvider repub={repub}>
            <PlusConfigProvider>
              <MyOCUserProvider myocData={myocData as MyOCData}>
                <ServicesProvider
                  NODE_ENV={ENV.NODE_ENV}
                  geolocationInfo={geolocationInfo}
                  labels={labels}
                  repub={repub}
                  siteConfig={siteConfig}
                >
                  <BetslipProvider persistedBetslipData={persistedBetslipData} userOddsFormat={oddsFormat}>
                    <Head />
                    <Body />
                  </BetslipProvider>
                </ServicesProvider>
              </MyOCUserProvider>
            </PlusConfigProvider>
          </RepubProvider>
        </FeatureFlagProvider>
      </html>
    </I18nextProvider>
  );
}

export default withSentry(App);
